import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuthContext } from '../../contexts/AuthContext';
import { useSalePage } from '../../contexts/SalePageContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../lib/firebase';
import { generatePromptPayQR } from '../../utils/promptpayQR';
import {
  ArrowLeft, Package, Check, TicketPercent, QrCode, Landmark, CreditCard, Copy,
  Upload, FileCheck2, X, ArrowRight, ShieldCheck, Download, Loader,
} from 'lucide-react';

const FONT = '"IBM Plex Sans Thai", system-ui, sans-serif';
const HEAD = "'Anuphan', sans-serif";
const INK = '#0E3A40';

// Real payment identity (same as PromptPayQRPayment / SlipUploadPayment)
const PROMPTPAY_PHONE = '0909532495';
const BANK = { name: 'ธนาคารกสิกรไทย', number: '024-3-20377-5', account: 'ธานัท เจริญสุข' };
const RECEIVER_ACCOUNT = '0243203775';

function billingLabel(bp?: string) {
  if (!bp) return 'รายเดือน';
  if (bp === 'yearly' || bp === 'annual') return 'รายปี';
  if (bp === 'halfyearly' || bp === 'semi_annual' || bp === '6month') return 'ราย 6 เดือน';
  return 'รายเดือน';
}
function benefitsFor(plan?: string): string[] {
  if (plan === 'premium_vip') return ['ทุกฟีเจอร์ใน Premium ครบ', 'สร้าง Sale Page ด้วย AI (10 ครั้ง/เดือน)', 'AI จัดหน้า+เขียนคำโฆษณาให้อัตโนมัติ', 'แก้ไขต่อได้อิสระหลังสร้าง'];
  if (plan === 'premium') return ['วิดีโอ 6 คลิป · ระบบชำระเงิน', 'รีวิว/FAQ ไม่จำกัด · แกลเลอรี่', 'นับถอยหลัง · ตารางเปรียบเทียบ', 'ฟอร์มติดต่อ · White Label'];
  return ['วิดีโอ 3 คลิป · รูป/ข้อความไม่จำกัด', 'YouTube ไม่จำกัด', 'ปุ่ม CTA · ราคาสินค้า', 'รีวิวลูกค้า · FAQ'];
}

export default function SalePageSlipCheckout() {
  const { user } = useAuthContext();
  const { refreshSubscription } = useSalePage();
  const navigate = useNavigate();

  const [checkoutData, setCheckoutData] = useState<any>(null);
  const [narrow, setNarrow] = useState(false);
  const [method, setMethod] = useState<'qr' | 'bank'>('qr');
  const [discountOpen, setDiscountOpen] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<{ code: string; percentage: number; amount: number } | null>(null);
  const [discountError, setDiscountError] = useState('');
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [copied, setCopied] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Load checkout data (from Renew via localStorage 'salePageCheckout')
  useEffect(() => {
    if (!user) {
      navigate('/login?redirect=/salepage/checkout-slip');
      return;
    }
    const raw = localStorage.getItem('salePageCheckout');
    if (!raw) { navigate('/salepage/renew'); return; }
    try {
      setCheckoutData(JSON.parse(raw));
    } catch {
      navigate('/salepage/renew');
    }
  }, [user, navigate]);

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 860);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const basePrice: number = checkoutData?.price ?? checkoutData?.amount ?? 0;
  const amount: number = appliedDiscount ? Math.max(0, basePrice - appliedDiscount.amount) : basePrice;
  const planName: string = checkoutData?.planName || (checkoutData?.plan === 'premium' ? 'Premium' : 'Basic');
  const planTier: string = checkoutData?.plan || 'basic';
  const benefits = benefitsFor(planTier);

  // Generate PromptPay QR whenever the payable amount changes
  useEffect(() => {
    let cancelled = false;
    if (!checkoutData || amount <= 0) { setQrUrl(''); return; }
    generatePromptPayQR(PROMPTPAY_PHONE, amount)
      .then((url) => { if (!cancelled) setQrUrl(url); })
      .catch((e) => { console.error('[SlipCheckout] QR error:', e); });
    return () => { cancelled = true; };
  }, [checkoutData, amount]);

  const applyDiscount = async () => {
    if (!discountCode.trim()) { setDiscountError('กรุณากรอกรหัสส่วนลด'); return; }
    setApplyingDiscount(true);
    setDiscountError('');
    try {
      const snap = await getDocs(query(
        collection(db, 'discountCodes'),
        where('code', '==', discountCode.trim().toUpperCase()),
        where('isActive', '==', true)
      ));
      if (snap.empty) { setDiscountError('รหัสส่วนลดไม่ถูกต้องหรือหมดอายุ'); return; }
      const d = snap.docs[0].data();
      if (d.expiresAt && d.expiresAt.toDate() < new Date()) { setDiscountError('รหัสส่วนลดหมดอายุแล้ว'); return; }
      if (d.usageLimit && d.usedCount >= d.usageLimit) { setDiscountError('รหัสส่วนลดถูกใช้งานครบแล้ว'); return; }
      const pct = Number(d.percentage) || 0;
      setAppliedDiscount({ code: d.code, percentage: pct, amount: Math.round((basePrice * pct) / 100) });
    } catch (e) {
      console.error('[SlipCheckout] discount error:', e);
      setDiscountError('เกิดข้อผิดพลาดในการตรวจสอบรหัส');
    } finally {
      setApplyingDiscount(false);
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setError('');
    if (!f) return;
    if (!f.type.startsWith('image/')) { setError('กรุณาเลือกไฟล์รูปภาพเท่านั้น'); return; }
    if (f.size > 5 * 1024 * 1024) { setError('ไฟล์มีขนาดใหญ่เกินไป (สูงสุด 5MB)'); return; }
    setFile(f);
  };

  const handleSubmit = async () => {
    if (!file || verifying || submitted || !user) return;
    setVerifying(true);
    setError('');
    try {
      const bp = checkoutData?.billingPeriod === 'yearly' || checkoutData?.billingCycle === 'yearly' ? 'yearly' : 'monthly';

      // 1) Upload the slip to R2 (get a public URL for the server to fetch)
      const up = await fetch('https://edumi-upload-api.edumi-subdomin.workers.dev/slip-upload', {
        method: 'POST',
        headers: {
          'Content-Type': file.type || 'image/jpeg',
          'X-File-Name': encodeURIComponent(file.name),
          'X-File-Size': file.size.toString(),
        },
        body: file,
      });
      if (!up.ok) throw new Error('อัปโหลดสลิปไม่สำเร็จ กรุณาลองใหม่');
      const { publicUrl } = await up.json();

      // 2) Server verifies the slip (SlipOK) AND activates the subscription — the client
      //    never writes the active subscription itself, so it can't be faked.
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'activateSalePageFromSlip');
      const res: any = await fn({
        fileUrl: publicUrl,
        plan: planTier,
        billingPeriod: bp,
        discountCode: appliedDiscount?.code || null,
      });
      if (!res?.data?.success) {
        setError(res?.data?.error || 'ตรวจสอบสลิปไม่ผ่าน กรุณาตรวจสอบยอดเงินและลองใหม่');
        return;
      }

      try { await refreshSubscription(); } catch { /* ignore */ }

      setSubmitted(true);
      localStorage.removeItem('salePageCheckout');
      setTimeout(() => navigate('/dashboard?payment=success'), 1500);
    } catch (e: any) {
      console.error('[SlipCheckout] submit error:', e);
      setError(e?.message || 'เกิดข้อผิดพลาดในการตรวจสอบสลิป');
    } finally {
      setVerifying(false);
    }
  };

  const copyAccount = () => {
    try { navigator.clipboard.writeText(RECEIVER_ACCOUNT); } catch { /* ignore */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const downloadQR = () => {
    if (!qrUrl) return;
    const a = document.createElement('a');
    a.href = qrUrl; a.download = `promptpay-${amount}baht.png`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const tabStyle = (active: boolean): React.CSSProperties => ({
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
    border: 0, borderRadius: 16, padding: '11px 10px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
    fontFamily: FONT,
    background: active ? 'linear-gradient(120deg, #00B4A1, #3A93B9)' : 'transparent',
    color: active ? '#fff' : '#5B7D80',
  });

  if (!checkoutData) {
    return (
      <div style={{ minHeight: '100vh', background: '#F3F8F8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT }}>
        <div style={{ textAlign: 'center' }}>
          <Loader style={{ width: 40, height: 40, color: '#00B4A1', margin: '0 auto 12px' }} className="animate-spin" />
          <p style={{ color: '#5B7D80' }}>กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  const canSubmit = !!file && !verifying && !submitted;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#F3F8F8', color: INK, fontFamily: FONT }}>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Anuphan:wght@400;500;600;700&family=IBM+Plex+Sans+Thai:wght@400;500;600&display=swap" rel="stylesheet" />
      </Helmet>

      {/* Header */}
      <header style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(243,248,248,0.92)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(14,58,64,0.08)' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto', padding: '14px clamp(16px,3vw,32px)', display: 'flex', alignItems: 'center', gap: 14 }}>
          <button type="button" onClick={() => navigate('/salepage/renew')} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, color: '#5B7D80', background: 'transparent', border: 0, cursor: 'pointer' }}>
            <ArrowLeft style={{ width: 16, height: 16 }} /> ย้อนกลับ
          </button>
          <div style={{ width: 1, height: 20, background: 'rgba(14,58,64,0.1)' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{ width: 28, height: 28, borderRadius: 9, background: 'linear-gradient(135deg, #00B4A1, #3A93B9)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: HEAD, fontWeight: 700, fontSize: 14 }}>S</span>
            <span style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 16.5 }}>SaleDi</span>
          </div>
        </div>
      </header>

      <main style={{ flex: 1, maxWidth: 1080, margin: '0 auto', width: '100%', padding: 'clamp(20px,3vw,30px) clamp(16px,3vw,32px) clamp(60px,8vw,90px)' }}>
        <div style={{ marginBottom: 'clamp(18px,2.4vw,24px)' }}>
          <h1 style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 'clamp(21px,2.4vw,26px)', margin: '0 0 4px', letterSpacing: '-0.02em' }}>ชำระเงิน</h1>
          <p style={{ fontSize: 13.5, color: '#5B7D80', margin: 0 }}>สแกน QR หรือโอนเงิน แล้วแนบสลิป ระบบตรวจสอบอัตโนมัติภายในไม่กี่วินาที</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '320px 1fr', gap: 'clamp(16px,2.2vw,22px)', alignItems: 'start' }}>
          {/* Summary column */}
          <div style={narrow ? { order: 1 } : { position: 'sticky', top: 76 }}>
            <div style={{ background: '#0E3A40', borderRadius: 20, padding: 'clamp(18px,2.2vw,22px)', color: '#fff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <span style={{ width: 36, height: 36, borderRadius: 11, background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                  <Package style={{ width: 17, height: 17, color: '#4FE0CC' }} />
                </span>
                <div>
                  <div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 16 }}>{planName}</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>{billingLabel(checkoutData?.billingPeriod || checkoutData?.billingCycle)} · Salepage</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '14px 0', borderTop: '1px solid rgba(255,255,255,0.12)', borderBottom: '1px solid rgba(255,255,255,0.12)', marginBottom: 16 }}>
                <span style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.7)' }}>ยอดที่ต้องชำระ</span>
                <span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 26, color: '#4FE0CC' }}>฿{amount.toLocaleString()}</span>
              </div>
              {appliedDiscount && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: '#4FE0CC', marginBottom: 12 }}>
                  <span>ส่วนลด {appliedDiscount.code} ({appliedDiscount.percentage}%)</span>
                  <span>-฿{appliedDiscount.amount.toLocaleString()}</span>
                </div>
              )}
              <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>สิ่งที่คุณจะได้รับ</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {benefits.map((b) => (
                  <div key={b} style={{ display: 'flex', gap: 9, fontSize: 13.5, color: 'rgba(255,255,255,0.88)' }}>
                    <Check style={{ width: 15, height: 15, color: '#4FE0CC', flex: '0 0 auto', marginTop: 1 }} /> {b}
                  </div>
                ))}
              </div>
            </div>

            {/* Discount */}
            <button type="button" onClick={() => setDiscountOpen((v) => !v)} style={{ width: '100%', marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px dashed rgba(14,58,64,0.18)', borderRadius: 14, padding: '12px 14px', fontSize: 13, color: '#3A93B9', cursor: 'pointer' }}>
              <TicketPercent style={{ width: 15, height: 15 }} /> {discountOpen ? 'ซ่อนช่องกรอกรหัสส่วนลด' : 'มีรหัสส่วนลด?'}
            </button>
            {discountOpen && (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="text" value={discountCode} onChange={(e) => setDiscountCode(e.target.value.toUpperCase())} placeholder="กรอกรหัสส่วนลด" disabled={!!appliedDiscount}
                    style={{ flex: 1, minWidth: 0, border: '1px solid rgba(14,58,64,0.14)', borderRadius: 11, padding: '10px 13px', fontSize: 13.5, background: '#fff', fontFamily: FONT }} />
                  {appliedDiscount ? (
                    <button type="button" onClick={() => { setAppliedDiscount(null); setDiscountCode(''); }} style={{ background: '#F3F8F8', color: '#5B7D80', border: '1px solid rgba(14,58,64,0.14)', borderRadius: 11, padding: '0 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>ลบ</button>
                  ) : (
                    <button type="button" onClick={applyDiscount} disabled={applyingDiscount} style={{ background: '#0E3A40', color: '#fff', border: 0, borderRadius: 11, padding: '0 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer', opacity: applyingDiscount ? 0.6 : 1 }}>{applyingDiscount ? '...' : 'ใช้รหัส'}</button>
                  )}
                </div>
                {discountError && <div style={{ fontSize: 12, color: '#EF4444', marginTop: 6 }}>{discountError}</div>}
              </div>
            )}
          </div>

          {/* Payment column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Method tabs */}
            <div style={{ background: '#fff', borderRadius: 20, padding: 5, border: '1px solid rgba(14,58,64,0.06)', display: 'flex', gap: 4 }}>
              <button type="button" onClick={() => setMethod('qr')} style={tabStyle(method === 'qr')}><QrCode style={{ width: 15, height: 15 }} /> QR PromptPay</button>
              <button type="button" onClick={() => setMethod('bank')} style={tabStyle(method === 'bank')}><Landmark style={{ width: 15, height: 15 }} /> โอนผ่านบัญชี</button>
              <div style={{ flex: 0.9, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, borderRadius: 16, padding: '11px 8px', fontSize: 12, color: '#B7C6C7', opacity: 0.7, whiteSpace: 'nowrap' }}>
                <CreditCard style={{ width: 14, height: 14 }} /> บัตร <span style={{ fontSize: 9.5, fontWeight: 700, color: '#FF7A45', background: 'rgba(255,122,69,0.12)', padding: '2px 6px', borderRadius: 999, marginLeft: 2 }}>เร็วๆนี้</span>
              </div>
            </div>

            {/* QR */}
            {method === 'qr' && (
              <div style={{ background: '#fff', borderRadius: 20, padding: 'clamp(20px,2.6vw,26px)', border: '1px solid rgba(14,58,64,0.06)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, textAlign: 'center' }}>
                <div style={{ width: 220, height: 220, borderRadius: 16, background: '#fff', border: '1px solid rgba(14,58,64,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
                  {qrUrl ? <img src={qrUrl} alt="PromptPay QR" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <Loader style={{ width: 32, height: 32, color: '#00B4A1' }} className="animate-spin" />}
                </div>
                <div>
                  <div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 20 }}>฿{amount.toLocaleString()}</div>
                  <div style={{ fontSize: 12.5, color: '#5B7D80' }}>สแกนด้วยแอปธนาคารของคุณ</div>
                </div>
                <button type="button" onClick={downloadQR} style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#F3F8F8', color: INK, border: '1px solid rgba(14,58,64,0.1)', borderRadius: 11, padding: '9px 16px', fontSize: 13, cursor: 'pointer' }}>
                  <Download style={{ width: 14, height: 14 }} /> บันทึก QR Code
                </button>
              </div>
            )}

            {/* Bank */}
            {method === 'bank' && (
              <div style={{ background: '#fff', borderRadius: 20, padding: 'clamp(18px,2.2vw,22px)', border: '1px solid rgba(14,58,64,0.06)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(14,58,64,0.07)' }}><span style={{ fontSize: 13, color: '#5B7D80' }}>ธนาคาร</span><span style={{ fontWeight: 600, fontSize: 13.5 }}>{BANK.name}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(14,58,64,0.07)' }}>
                  <span style={{ fontSize: 13, color: '#5B7D80' }}>เลขบัญชี</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 700, fontFamily: HEAD, fontSize: 14.5, letterSpacing: '0.02em' }}>{BANK.number}</span>
                    <button type="button" onClick={copyAccount} style={{ background: 'none', border: 0, cursor: 'pointer', padding: 4, color: '#3A93B9', display: 'flex' }}>{copied ? <Check style={{ width: 14, height: 14 }} /> : <Copy style={{ width: 14, height: 14 }} />}</button>
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(14,58,64,0.07)' }}><span style={{ fontSize: 13, color: '#5B7D80' }}>ชื่อบัญชี</span><span style={{ fontWeight: 600, fontSize: 13.5 }}>{BANK.account}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '12px 0' }}><span style={{ fontSize: 13, color: '#5B7D80' }}>จำนวนเงินที่ต้องโอน</span><span style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 18, color: '#00B4A1' }}>฿{amount.toLocaleString()}</span></div>
              </div>
            )}

            {/* Slip upload */}
            <div style={{ background: '#fff', borderRadius: 20, padding: 'clamp(18px,2.2vw,22px)', border: '1px solid rgba(14,58,64,0.06)' }}>
              {!file ? (
                <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center', border: '1.5px dashed rgba(14,58,64,0.18)', borderRadius: 16, padding: 'clamp(22px,3vw,28px) 16px', cursor: 'pointer' }}>
                  <span style={{ width: 44, height: 44, borderRadius: 999, background: 'rgba(58,147,185,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Upload style={{ width: 19, height: 19, color: '#3A93B9' }} /></span>
                  <div style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 14.5 }}>แนบสลิปการโอนเงิน</div>
                  <div style={{ fontSize: 12, color: '#8AA6A8' }}>คลิกเพื่อเลือกไฟล์ · JPG, PNG สูงสุด 5MB</div>
                  <input type="file" accept="image/*" onChange={onFileChange} style={{ display: 'none' }} />
                </label>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 14, background: 'rgba(0,180,161,0.07)' }}>
                  <span style={{ width: 38, height: 38, borderRadius: 11, background: 'rgba(0,180,161,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}><FileCheck2 style={{ width: 17, height: 17, color: '#00B4A1' }} /></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.name}</div>
                    <div style={{ fontSize: 12, color: '#5B7D80' }}>แนบไฟล์แล้ว · {(file.size / 1024).toFixed(0)} KB</div>
                  </div>
                  {!verifying && !submitted && (
                    <button type="button" onClick={() => setFile(null)} style={{ background: 'none', border: 0, cursor: 'pointer', padding: 6, color: '#8AA6A8' }}><X style={{ width: 16, height: 16 }} /></button>
                  )}
                </div>
              )}
              {error && <div style={{ marginTop: 12, fontSize: 13, color: '#B91C1C', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 12, padding: '10px 14px' }}>{error}</div>}
            </div>

            <button type="button" onClick={handleSubmit} disabled={!canSubmit}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, border: 0, borderRadius: 14, padding: 15, fontFamily: HEAD, fontSize: 15.5, fontWeight: 600,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                background: submitted ? '#00B4A1' : (canSubmit ? 'linear-gradient(120deg, #00B4A1, #3A93B9)' : '#D8E4E4'),
                color: canSubmit || submitted ? '#fff' : '#94A8A9',
                boxShadow: canSubmit ? '0 16px 32px -16px rgba(0,180,161,0.5)' : 'none' }}>
              {submitted ? <>ชำระเงินสำเร็จ • เปิดใช้งานแล้ว <Check style={{ width: 16, height: 16 }} /></>
                : verifying ? <><Loader style={{ width: 16, height: 16 }} className="animate-spin" /> กำลังตรวจสอบสลิป...</>
                : <>ยืนยันการชำระเงิน <ArrowRight style={{ width: 16, height: 16 }} /></>}
            </button>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 12, color: '#5B7D80' }}>
              <ShieldCheck style={{ width: 13, height: 13, color: '#00B4A1' }} /> ระบบตรวจสอบสลิปอัตโนมัติด้วย SlipOK ทราบผลภายในไม่กี่วินาที
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
